import { useEffect, useMemo, useState } from 'react';
import { strapiService, type SpotlightListItem } from '@/services/StrapiService';
import { useVisibilityContext } from '@/hooks/visibility/useVisibilityContext';
import { visibleSpotlights, type SpotlightCardModel } from '@/components/Spotlight/spotlight';

export interface UseSpotlightsResult {
    cards: SpotlightCardModel[];
    isLoading: boolean;
    error: string | null;
}

export const useSpotlights = (): UseSpotlightsResult => {
    const { context, ready } = useVisibilityContext();
    const [items, setItems] = useState<SpotlightListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        strapiService
            .listSpotlights()
            .then((list) => {
                if (!cancelled) setItems(list);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load spotlights');
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const cards = useMemo(
        () => (ready ? visibleSpotlights(items, context) : []),
        [items, context, ready],
    );

    return { cards, isLoading: isLoading || !ready, error };
};
